// =============================================================================
// staticSite/astroDeploy — deploy przez silnik Astro (kontener build-worker).
//
// Panel: sklada SiteModel + redirecty + inwentarz obrazow + favicony (canvas)
//   -> POST /api/static-build/build (202 {jobId}) -> polling GET /build/:id
//   -> po sukcesie PANEL zapisuje static_deploys / static_sites (swieza sesja;
//      worker dopisuje tylko build_log). Galaz 'browser' (runDeploy) nietknieta.
// Auth: nginx auth_request (X-Supabase-Auth z interceptora) + Authorization
//   (JWT sesji) passthrough dla workera, ktory pisze nim build_log (RLS).
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";
import { buildFaviconBundle } from "@/lib/faviconBuilder";
import type { ProgressCallback, StaticSiteRow } from "./types";
import { DEFAULT_SITE_SETTINGS } from "./types";
import type { BuildJobView, BuildRequest, BuildFavicon } from "./buildRequest";
import { assembleSite } from "./siteAssembler";
import { collectAssets } from "./assetCollector";
import { renderRedirectsFile, renderHeadersFile } from "./redirectsBuilder";
import { bytesToBase64 } from "./cfPagesDeployer";
import { createPagesProject, getPagesProject } from "./cfPagesApi";
import { projectNameFor } from "./cfAccountAllocator";
import { buildAllRedirects, updateStaticSite, type DeployArgs, type DeployResult } from "./migrationRunner";

const PREFIX = "STATIC";
const BUILD_API = "/api/static-build";
const POLL_MS = 2000;
/** Bez postepu przez tyle ms = uznajemy, ze worker padl */
const STALL_MS = 15 * 60 * 1000;

async function sessionJwt(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Brak sesji — zaloguj się ponownie");
  return token;
}

async function buildApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const jwt = await sessionJwt();
  const res = await fetch(`${BUILD_API}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}`, ...(init.headers || {}) },
  });
  let json: any = null;
  try { json = await res.json(); } catch { /* nie-JSON */ }
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    throw new Error("Kontener build-worker nie odpowiada (502). Sprawdź w Coolify, czy Application build-worker-pbnsd666 działa.");
  }
  if (!res.ok) {
    const details = Array.isArray(json?.details) ? `: ${json.details.join("; ")}` : "";
    throw new Error(`${json?.error || `HTTP ${res.status}`}${details}`);
  }
  return json as T;
}

async function getBuildJob(jobId: string): Promise<BuildJobView> {
  return buildApi<BuildJobView>(`/build/${encodeURIComponent(jobId)}`);
}

export async function checkBuildWorker(): Promise<{ ok: boolean; message: string }> {
  try {
    const h = await buildApi<{ status: string; queue: { running: number; pending: number } }>("/health");
    return { ok: h.status === "ok", message: `build-worker OK · w toku ${h.queue.running}, w kolejce ${h.queue.pending}` };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) };
  }
}

export interface AstroDeployArgs extends DeployArgs {
  /** main = produkcyjny adres pages.dev; preview = <branch>.<projekt>.pages.dev (podglad) */
  branch?: "main" | "preview";
}

export async function runAstroDeploy(site: StaticSiteRow, args: AstroDeployArgs): Promise<DeployResult> {
  const progress = args.onProgress || (() => {});
  const settings = { ...DEFAULT_SITE_SETTINGS, ...(site.settings || {}) };
  const branch = args.branch || "main";
  const warnings: string[] = [];

  // 1. Model + redirecty + inwentarz obrazow (bez bajtow — pobierze worker)
  const model = await assembleSite(site.map_id, { settings, fetchWpSitemap: true, fetchWpRest: true, onProgress: progress });
  warnings.push(...model.warnings);
  const redirects = await buildAllRedirects(site, model);
  const red = renderRedirectsFile(redirects, site.domain);
  warnings.push(...red.warnings);
  const assets = collectAssets(model).map((a) => ({ src: a.src, path: a.path }));

  // 2. Favicony (canvas — tylko przegladarka)
  const favicons: BuildFavicon[] = [];
  if (model.brand?.logoSvg && typeof document !== "undefined") {
    try {
      for (const f of await buildFaviconBundle(model.brand.logoSvg, model.brand.themeColor)) favicons.push({ path: f.name, base64: bytesToBase64(f.bytes) });
    } catch (e) {
      warnings.push(`Favicony pominięte: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // 3. Projekt Pages (jak runDeploy)
  progress({ type: "phase", phase: "upload", message: "Sprawdzam projekt Cloudflare Pages" });
  const projectName = site.pages_project_name || projectNameFor(site.domain);
  let project = await getPagesProject(args.account.api_token, args.accountId, projectName);
  if (!project) {
    project = await createPagesProject(args.account.api_token, args.accountId, projectName, "main");
    logPublic(PREFIX, `Utworzono projekt Pages ${projectName} na koncie ${args.account.label}`);
  }
  const subdomain = project.subdomain || `${projectName}.pages.dev`;
  await updateStaticSite(site.id, {
    pages_cf_account_id: args.account.id,
    pages_cf_account_remote_id: args.accountId,
    pages_project_name: projectName,
    pages_subdomain: subdomain,
  });

  // 4. Wiersz deployu (status building) + zadanie do workera
  const { data: deployRow, error: insErr } = await (supabase as any)
    .from("static_deploys")
    .insert({ site_id: site.id, branch, trigger: args.trigger || "manual", status: "building" })
    .select("id")
    .single();
  if (insErr) throw new Error(`static_deploys: ${insErr.message}`);
  const deployId = deployRow.id as string;

  const request: BuildRequest = {
    deployId,
    theme: settings.astro_theme || "brand",
    themeSeed: site.theme_seed,
    design: settings.design ?? null,
    site: model,
    redirects,
    redirectsText: red.content,
    headersText: renderHeadersFile(),
    assets,
    favicons,
    cf: { token: args.account.api_token, accountId: args.accountId, project: projectName, branch, pagesSubdomain: subdomain },
  };

  try {
    progress({ type: "phase", phase: "render", message: `Wysyłam dane do build-workera (${model.pages.length} stron, ${assets.length} obrazów)` });
    const { jobId } = await buildApi<{ jobId: string }>("/build", { method: "POST", body: JSON.stringify(request) });
    logPublic(PREFIX, `Astro build ${site.domain}: job ${jobId}, deploy ${deployId}, branch ${branch}`);

    // 5. Polling
    const job = await pollJob(jobId, progress);
    if (job.status !== "success" || !job.result) throw new Error(job.error || "Build zakończony bez wyniku");
    const r = job.result;
    warnings.push(...r.warnings);

    // 6. Finalny status — pisze PANEL
    const url = r.url || `https://${branch === "preview" ? `preview.${subdomain}` : subdomain}`;
    await (supabase as any).from("static_deploys").update({
      status: "success",
      cf_deployment_id: r.cfDeploymentId,
      cf_url: url,
      file_count: r.fileCount,
      bytes_total: r.bytesTotal,
      uploaded_count: r.uploadedCount,
      parity_report: (r.parity as any)?.summary ?? null,
      finished_at: new Date().toISOString(),
    }).eq("id", deployId);
    if (branch === "main") {
      await updateStaticSite(site.id, {
        status: site.status === "live" ? "live" : "deployed",
        last_deploy_id: deployId,
        last_parity: (r.parity as StaticSiteRow["last_parity"]) ?? site.last_parity,
        asset_manifest: { ...(site.asset_manifest || {}), ...r.assetManifest } as StaticSiteRow["asset_manifest"],
        needs_redeploy: false,
      });
    }
    progress({ type: "done", message: `Wdrożone: ${url}` });
    logPublic(PREFIX, `Astro deploy ${site.domain} -> ${url}: ${r.fileCount} plików, wgrano ${r.uploadedCount}, build ${Math.round(r.buildMs / 1000)} s`);
    return { url, subdomain, project: projectName, deployId, fileCount: r.fileCount, uploadedCount: r.uploadedCount, bytesTotal: r.bytesTotal, warnings, assetsFailed: r.assetsFailed };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    await (supabase as any).from("static_deploys").update({ status: "failed", error: msg, finished_at: new Date().toISOString() }).eq("id", deployId);
    logError(PREFIX, "Astro deploy nieudany", e);
    progress({ type: "error", message: msg });
    throw e;
  }
}

const STAGE_LABEL: Record<BuildJobView["stage"], string> = {
  queued: "W kolejce build-workera",
  prepare: "Przygotowuję build",
  assets: "Pobieram obrazy",
  build: "Buduję stronę (Astro)",
  parity: "Sprawdzam bramki G1–G6",
  upload: "Wysyłam pliki na Cloudflare Pages",
  deploy: "Czekam na publikację w Cloudflare",
  done: "Gotowe",
};

async function pollJob(jobId: string, progress: ProgressCallback): Promise<BuildJobView> {
  let lastChange = Date.now();
  let lastKey = "";
  for (;;) {
    const job = await getBuildJob(jobId);
    const key = `${job.stage}|${job.progress?.current ?? ""}|${job.log_lines}`;
    if (key !== lastKey) { lastKey = key; lastChange = Date.now(); }
    if (job.status === "success" || job.status === "failed") return job;
    const phase = job.stage === "assets" ? "assets" : job.stage === "build" || job.stage === "prepare" ? "render" : job.stage === "parity" ? "parity" : job.stage === "deploy" ? "deploy" : "upload";
    const tail = job.log_tail[job.log_tail.length - 1] || "";
    if (job.progress) progress({ type: "progress", phase, current: job.progress.current, total: job.progress.total, message: STAGE_LABEL[job.stage] });
    else progress({ type: "phase", phase, message: tail ? `${STAGE_LABEL[job.stage]} · ${tail.replace(/^\d\d:\d\d:\d\d /, "").slice(0, 90)}` : STAGE_LABEL[job.stage] });
    if (Date.now() - lastChange > STALL_MS) throw new Error("Build-worker nie zgłasza postępu od 15 minut — deploy oznaczony jako nieudany (kontener mógł się zrestartować).");
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
}
